import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";

interface DuplicateWarningProps {
  duplicateCount: number;
  entityType: "CONTACT" | "COMPANY" | "SALE";
  strategy: "skip" | "update";
  onStrategyChange: (strategy: "skip" | "update") => void;
}

const DuplicateWarning: React.FC<DuplicateWarningProps> = ({
  duplicateCount,
  entityType,
  strategy,
  onStrategyChange,
}) => {
  const { t } = useTranslation();

  const entityLabel =
    entityType === "CONTACT" ? t("contact") : entityType === "COMPANY" ? t("account") : t("deal");
  const entityLabelPlural =
    entityType === "CONTACT"
      ? t("contacts")
      : entityType === "COMPANY"
        ? t("accounts")
        : t("deals");
  const matchKey =
    entityType === "CONTACT"
      ? t("by email")
      : entityType === "COMPANY"
        ? t("by domain/name")
        : t("by deal name");

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-yellow-800 mb-2">
            {t("Duplicate records detected")}
          </div>
          <div className="text-sm text-yellow-700 mb-4">
            {duplicateCount === 1
              ? t("{entity} has", { entity: entityLabel })
              : t("{entity} have", { entity: entityLabelPlural })}{" "}
            {duplicateCount} {t("duplicate entries in your CSV file")} ({matchKey})
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium text-yellow-800">
              {t("Duplicate handling strategy")}:
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`duplicate-strategy-${entityType}`}
                  value="update"
                  checked={strategy === "update"}
                  onChange={() => onStrategyChange("update")}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-yellow-800">
                  {t("Update existing records")} ({matchKey})
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`duplicate-strategy-${entityType}`}
                  value="skip"
                  checked={strategy === "skip"}
                  onChange={() => onStrategyChange("skip")}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-yellow-800">{t("Skip duplicate records")}</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateWarning;
